import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Countries } from '../assets/country-dataset';
import { CountryData } from '../data-types/countryList';
import { globeSVG } from '../assets/globe-svg';
import { Country } from '../data-types/country';
import { type CountryCode, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

@Component({
  selector: 'app-angular-phone-number-input',
  standalone: false,
  // imports: [CommonModule, FormsModule],
  templateUrl: './angular-phone-number-input.component.html',
  styleUrl: './angular-phone-number-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AngularPhoneNumberInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AngularPhoneNumberInputComponent),
      multi: true,
    },
  ],
})
export class AngularPhoneNumberInputComponent implements ControlValueAccessor, Validator, OnChanges {
  @Input() error: boolean | undefined = false;
  @Input() defaultCountry!: string;

  /** Last value written from the form (used to re-parse when `defaultCountry` changes). */
  private lastPatchedValue: string | null = null;
  @Input() preferredCountries!: string[];
  @Input() border: boolean = true;

  sanitizer = inject(DomSanitizer);
  countryList: CountryData = Countries;
  dataSet: CountryData = Countries;
  showModal: boolean = false;
  selectedCountry!: Country;
  inputFocused: boolean = false;
  disabled: boolean = false;
  value!: string;
  globeSVG: string = globeSVG;

  @ViewChild('modalContainer') modalRef!: ElementRef<HTMLElement>;
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const clickedInside = this.modalRef?.nativeElement.contains(event.target as Node);

    if (!clickedInside && this.showModal) {
      this.showModal = false;
    }
  }

  /** Callback function triggered on value change. */
  onChange: (value: string) => void = () => { };

  /** Callback function triggered on input touch. */
  onTouched: () => void = () => { };

  /** Handles the input blur event. */
  onBlur = () => {
    this.inputFocused = false;
  };

  /**
   * Handles the input focus event.
   */
  onFocus = (): void => {
    this.inputFocused = true;
  };

  /**
   * Writes a new value to the input.
   * When the model is cleared (null/empty), syncs internal state so the view clears.
   * @param value - The new value from the form control.
   */
  writeValue = (value: string): void => {
    this.lastPatchedValue = value;
    if (value != null && value !== '') {
      this.extractValues(value);
    } else {
      this.value = '';
      this.setDefaultCountry();
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['defaultCountry'] || changes['defaultCountry'].firstChange) {
      return;
    }
    const v = this.lastPatchedValue;
    if (v != null && String(v).trim() !== '') {
      this.extractValues(String(v));
    }
  }

  /**
   * Registers a callback function to be executed when the input value changes.
   * @param fn - The callback function.
   */
  registerOnChange = (fn: () => void): void => {
    this.onChange = fn;
  };

  /**
   * Registers a callback function to be executed when the input is touched.
   * @param fn - The callback function.
   */
  registerOnTouched = (fn: () => void): void => {
    this.onTouched = fn;
  };

  /**
   * Sets the disabled state of the input.
   * @param isDisabled - The disabled state.
   */
  setDisabledState = (isDisabled: boolean): void => {
    this.disabled = isDisabled;
  };

  /**
   * Initializes default country and preferred countries on component initialization.
   * Calls setDefaultCountry and setPreferredCountries methods.
   */
  ngOnInit() {
    this.setDefaultCountry();
    this.setPreferredCountries();
  }

  /**
   * Sets the default country based on the provided defaultCountry value.
   * If the default country exists in the countryList, sets the selectedCountry accordingly.
   */
  setDefaultCountry = () => {
    const defCountryData = this.countryList[this.defaultCountry];
    this.selectedCountry = defCountryData ?? null;
  }

  /**
   * Sets preferred countries based on the provided preferredCountries array.
   * Filters the countryList to contain only the preferred countries if they exist.
   */
  setPreferredCountries = () => {
    if (this.preferredCountries && this.preferredCountries.length > 0) {
      // Filter the countries to include only preferred countries
      const preferredCountryList = Object.fromEntries(
        Object.entries(Countries).filter(([code]) => this.preferredCountries.includes(code))
      );

      // If preferred countries are found, update the countryList
      if (Object.keys(preferredCountryList).length > 0) {
        this.countryList = preferredCountryList;
      }
    }
  }

  /**
   * Toggles the modal visibility and sets the dataSet to the current countryList.
   */
  toggleModal(event?: MouseEvent): void {
    event?.stopPropagation();
    this.dataSet = this.countryList
    this.showModal = !this.showModal;

    setTimeout(() => {
      this.focusSearchInput();
    }, 0);
  }

  private focusSearchInput(): void {
    if (this.searchInputRef && this.searchInputRef.nativeElement) {
      this.searchInputRef.nativeElement.focus();
    }
  }

  /**
   * Sets the full phone number by concatenating the country dial code and the input value.
   * Updates the value using the onChange callback.
   * @returns {void}
   */
  setFullPhoneNumber = () => {
    const fullPhoneNumber = this.selectedCountry ? this.selectedCountry.dialCode + (this.value || '') : this.value || '';
    // Trigger the onChange callback with the updated full phone number
    this.onChange(fullPhoneNumber);
  }

  /**
   * Selects a country and emits the corresponding event.
   * @param {Country} data - The selected country data.
   */
  selectCountry(data: { key: string; value: CountryData[string] }): void {
    this.selectedCountry = data.value;
    this.setFullPhoneNumber();
    this.toggleModal();
  }

  /**
   * Extracts the country code and phone number from the provided phone number string.
   * @param {string} phoneNumber - The input phone number string.
   * @returns {void}
   */
  extractValues = (phoneNumber: string) => {
    const raw = typeof phoneNumber === 'string' ? phoneNumber.trim() : String(phoneNumber ?? '').trim();
    if (!raw) {
      this.value = '';
      this.setDefaultCountry();
      return;
    }

    const countryCodes = Object.keys(Countries);
    const sortedCodes = [...countryCodes].sort(
      (a, b) => Countries[b].dialCode.length - Countries[a].dialCode.length,
    );

    for (const code of sortedCodes) {
      if (raw.startsWith(Countries[code].dialCode)) {
        const country = Countries[code];
        this.value = raw.substring(country.dialCode.length);
        this.selectedCountry = country;
        return;
      }
    }

    const regionIso = this.defaultCountry as CountryCode | undefined;
    if (regionIso && Countries[regionIso]) {
      try {
        const parsed = parsePhoneNumber(raw, regionIso);
        const iso = parsed.country;
        if (parsed.isValid() && iso && Countries[iso]) {
          this.selectedCountry = Countries[iso];
          this.value = String(parsed.nationalNumber);
          return;
        }
      } catch {
        // Not a parseable national number for this region; fall through.
      }
      this.selectedCountry = Countries[regionIso];
      this.value = raw.replace(/^\+/, '').replace(/\D/g, '');
      return;
    }

    this.value = raw.replace('+', '').replace(/\D/g, '');
  };

  /**
   * Tries to determine the selected country based on the provided input value.
   * Updates the selected country and value if a matching country code is found.
   * @returns {void}
   */
  getCountryWithInput() {
    if (this.value) {
      // temp number to hold the input value
      const tempNum = '+' + this.value.toString();
      if (!this.selectedCountry && tempNum.length === 5) {
        const countryCodes = Object.keys(Countries);
        const sortedCodes = countryCodes.sort((a, b) => Countries[b].dialCode.length - Countries[a].dialCode.length);
        for (const code of sortedCodes) {
          if (tempNum.startsWith(Countries[code].dialCode)) {
            this.selectedCountry = Countries[code];
            // remove the country code from input value
            this.value = this.value.toString().substring(this.selectedCountry.dialCode.length - 1);
            break;
          }
        }
      }
    }
  }

  /**
   * Emits the current input value.
   */
  onInputChanged(): void {
    this.setFullPhoneNumber();
    this.getCountryWithInput();
  }

  /**
   * Prevents non-numeric characters from being entered into the input.
   */
  onKeyDown(event: KeyboardEvent): void {
    const allowedControlKeys = [
      'Backspace',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      'Home',
      'End',
    ];

    if (allowedControlKeys.includes(event.key)) {
      return;
    }

    // Allow only digits 0-9
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * Filters the countries based on the provided search term and updates the dataset.
   * @param {string} searchTerm - The term to search for within country names or dial codes.
   * @returns {void}
   */
  filterCountries(searchTerm: string): void {
    // Convert the searchTerm to lowercase for case-insensitive matching
    searchTerm = searchTerm.toLowerCase();
    // Filter the countryList based on the searchTerm and update dataSet
    this.dataSet = Object.keys(this.countryList)
      .filter(code =>
        this.countryList[code].country.toLowerCase().includes(searchTerm) ||
        this.countryList[code].dialCode.includes(searchTerm)
      )
      .reduce((obj: CountryData, code) => {
        // Reduce the filtered keys back into an object
        obj[code] = this.countryList[code];
        return obj;
      }, {});
  }

  /**
   * Handles changes in the search input by invoking the filterCountries method with the input value.
   * @param {Event} event - The event object from the search input.
   * @returns {void}
   */
  onSearchChange(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    this.filterCountries(searchTerm);
  }

  /**
   * Retrieves the sanitized HTML representation of an icon string.
   * @param {string | undefined} iconString - The string representation of an SVG icon.
   * @returns {SafeHtml | undefined} The sanitized HTML representation of the icon string.
   */
  getIcon = (iconString: string | undefined): SafeHtml | undefined => {
    if (iconString) {
      return this.sanitizer.bypassSecurityTrustHtml(iconString);
    }
    return undefined;
  };

  /**
   * Validates the input control.
   * @param control - The AbstractControl instance.
   * @returns ValidationErrors if value is null.
   */
  validate = (control: AbstractControl): ValidationErrors | null => {
    const fullPhoneNumber = this.selectedCountry ? this.selectedCountry.dialCode + (this.value || '') : '+' + this.value || '';
    // if no value update and inputted control has validation with required or return true
    if (!this.value || this.value === '') {
      if (control.hasValidator(Validators.required)) return { required: true }
      return null;
    }

    // check valid phone number or not with libphonenumber-js
    if (!isValidPhoneNumber(fullPhoneNumber.toString())) {
      return { status: "INVALID" };
    }

    return null;
  };
}
